// ============================================================================
// Effective Values Utility
// Shared utility functions for resolving effective pricing/charges/fees
// Used by both Site Config and Sites Dashboard
// ============================================================================

import { Pricing, SiteOverageFee, SiteImposedCharge, StoreOverageFee } from '../types/site';
import { StorePricing, StoreCharge } from '../services/configService';
import { getLocalDateString, getTodayString, formatDateShort } from './dateUtils';

// ============================================================================
// Types
// ============================================================================

export type EffectiveSource = 'store' | 'site';

export interface EffectiveItem<T> {
  data: T;
  source: EffectiveSource;
  label?: string;
}

export interface EffectivePricingResult {
  price: number;
  source: EffectiveSource;
  description: string;
  hasSpecialPricing: boolean;
  specialPricingDateRange?: string;
}

export interface EffectiveOverageChargeResult {
  fee_type: string;
  base_included: number;
  max_allowed: number;
  amount: number;
  flat: boolean;
  frequency: string;
  source: EffectiveSource;
}

export interface EffectiveFeeResult {
  item: string;
  amount: number;
  flat: boolean;
  frequency: string;
  fee_trigger?: string;
  source: EffectiveSource;
}

// ============================================================================
// Pricing Resolution
// ============================================================================

/**
 * Resolve effective pricing for a site on a given date.
 * Hierarchy: Site special pricing > Store base rate by site type > Store default rate
 */
export function resolveEffectivePricing(
  siteTypeId: number | undefined,
  sitePricing: Pricing[],
  storePricing: StorePricing[],
  date?: Date
): EffectivePricingResult {
  // No explicit date = "right now" at the store, not in the viewer's browser.
  const dateStr = date ? getLocalDateString(date) : getTodayString();

  // Check for active site special pricing for the given date
  const activeSpecialPricing = sitePricing.find(p => {
    if (!p.active) return false;
    const startOk = !p.effective_date || p.effective_date <= dateStr;
    const endOk = !p.end_date || p.end_date >= dateStr;
    return startOk && endOk;
  });

  if (activeSpecialPricing) {
    const startStr = activeSpecialPricing.effective_date
      ? formatDateShort(activeSpecialPricing.effective_date)
      : 'Start';
    const endStr = activeSpecialPricing.end_date
      ? formatDateShort(activeSpecialPricing.end_date)
      : 'Ongoing';

    return {
      price: activeSpecialPricing.price,
      source: 'site',
      description: 'Site Special Pricing',
      hasSpecialPricing: true,
      specialPricingDateRange: `${startStr} - ${endStr}`,
    };
  }

  // Fall back to store base rate by site type
  if (siteTypeId) {
    const matchingRate = storePricing.find(r => r.site_type_id === siteTypeId && r.active);
    if (matchingRate) {
      return {
        price: matchingRate.price,
        source: 'store',
        description: matchingRate.site_type_name || 'Site Type Rate',
        hasSpecialPricing: false,
      };
    }
  }

  // Fall back to default store rate (no site_type_id)
  const defaultRate = storePricing.find(r => !r.site_type_id && r.active);
  if (defaultRate) {
    return {
      price: defaultRate.price,
      source: 'store',
      description: 'Default Store Rate',
      hasSpecialPricing: false,
    };
  }

  return {
    price: 0,
    source: 'store',
    description: 'No pricing configured',
    hasSpecialPricing: false,
  };
}

// ============================================================================
// Overage Charges Resolution (Override Model)
// ============================================================================

/**
 * Resolve effective overage charges for a site.
 * Site charges override store charges for matching fee_type.
 */
export function resolveEffectiveOverageCharges(
  siteOverageFees: SiteOverageFee[],
  storeOverageFees: StoreOverageFee[]
): EffectiveOverageChargeResult[] {
  const effective: EffectiveOverageChargeResult[] = [];
  const siteOverrideTypes = new Set(siteOverageFees.filter(f => f.active).map(f => f.fee_type));

  // Add store fees that are not overridden by site fees
  for (const storeFee of storeOverageFees.filter(f => f.active)) {
    if (!siteOverrideTypes.has(storeFee.fee_type)) {
      effective.push({
        fee_type: storeFee.fee_type,
        base_included: storeFee.base_included,
        max_allowed: storeFee.max_allowed,
        amount: storeFee.amount,
        flat: storeFee.flat,
        frequency: storeFee.frequency,
        source: 'store',
      });
    }
  }

  // Add all active site fees (these override store fees for same fee_type)
  for (const siteFee of siteOverageFees.filter(f => f.active)) {
    effective.push({
      fee_type: siteFee.fee_type,
      base_included: siteFee.base_included,
      max_allowed: siteFee.max_allowed,
      amount: siteFee.amount,
      flat: siteFee.flat,
      frequency: siteFee.frequency,
      source: 'site',
    });
  }

  return effective;
}

// ============================================================================
// Fees Resolution (Additive Model)
// ============================================================================

/**
 * Resolve effective fees for a site.
 * ADDITIVE model: both store and site fees apply together.
 */
export function resolveEffectiveFees(
  siteCharges: SiteImposedCharge[],
  storeCharges: StoreCharge[]
): EffectiveFeeResult[] {
  const effective: EffectiveFeeResult[] = [];

  // All active store charges apply
  for (const storeCharge of storeCharges.filter(c => c.active)) {
    effective.push({
      item: storeCharge.item,
      amount: storeCharge.amount,
      flat: storeCharge.flat,
      frequency: storeCharge.frequency,
      fee_trigger: storeCharge.fee_trigger,
      source: 'store',
    });
  }

  // All active site charges are additional
  for (const siteCharge of siteCharges.filter(c => c.active)) {
    effective.push({
      item: siteCharge.item,
      amount: siteCharge.amount,
      flat: siteCharge.flat,
      frequency: siteCharge.frequency,
      fee_trigger: siteCharge.fee_trigger,
      source: 'site',
    });
  }

  return effective;
}

// ============================================================================
// Generic Effective Items Helpers
// ============================================================================

/**
 * Compute effective items using OVERRIDE model.
 * Site items override store items when they match by a given key.
 */
export function computeOverrideEffectiveItems<TStore, TSite, TKey>(
  storeItems: TStore[],
  siteItems: TSite[],
  getStoreKey: (item: TStore) => TKey,
  getSiteKey: (item: TSite) => TKey
): EffectiveItem<TStore | TSite>[] {
  const effective: EffectiveItem<TStore | TSite>[] = [];
  const siteKeysUsed = new Set<TKey>();

  // First, process store items and check for site overrides
  for (const storeItem of storeItems) {
    const key = getStoreKey(storeItem);
    const siteOverride = siteItems.find(s => getSiteKey(s) === key);

    if (siteOverride) {
      effective.push({ data: siteOverride, source: 'site' });
      siteKeysUsed.add(key);
    } else {
      effective.push({ data: storeItem, source: 'store' });
    }
  }

  // Add any site items that don't have a store counterpart
  for (const siteItem of siteItems) {
    const key = getSiteKey(siteItem);
    if (!siteKeysUsed.has(key)) {
      effective.push({ data: siteItem, source: 'site' });
    }
  }

  return effective;
}

/**
 * Compute effective items using ADDITIVE model.
 * Both store and site items apply together.
 */
export function computeAdditiveEffectiveItems<TStore, TSite>(
  storeItems: TStore[],
  siteItems: TSite[]
): EffectiveItem<TStore | TSite>[] {
  const effective: EffectiveItem<TStore | TSite>[] = [];

  // All store items apply
  for (const storeItem of storeItems) {
    effective.push({ data: storeItem, source: 'store' });
  }

  // All site items are additional
  for (const siteItem of siteItems) {
    effective.push({ data: siteItem, source: 'site' });
  }

  return effective;
}

// ============================================================================
// Helper Functions
// ============================================================================

/**
 * Check if a pricing period is currently active
 */
export function isPricingActive(pricing: Pricing, date?: Date): boolean {
  if (!pricing.active) return false;
  const dateStr = date ? getLocalDateString(date) : getTodayString();
  const startOk = !pricing.effective_date || pricing.effective_date <= dateStr;
  const endOk = !pricing.end_date || pricing.end_date >= dateStr;
  return startOk && endOk;
}

/**
 * Format a date range for display
 */
export function formatDateRange(start?: string, end?: string): string {
  if (!start && !end) return 'Always';
  const startStr = start ? formatDateShort(start) : 'Start';
  const endStr = end ? formatDateShort(end) : 'Ongoing';
  return `${startStr} - ${endStr}`;
}
